import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isHiddenRoute, isUuid, labelForSegment } from '@/utils/route-title';
import { ChevronRightIcon, HomeIcon } from '../icons';

/**
 * Maps route path segments to human-readable labels.
 * Dynamic segments (e.g. :id) are handled separately.
 */
interface BreadcrumbItem {
  label: string;
  href: string;
}

export default function Breadcrumb() {
  const location = useLocation();

  const crumbs = useMemo(() => {
    const { pathname } = location;

    // Hide breadcrumbs on certain routes
    if (isHiddenRoute(pathname)) return [];

    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return [];

    const items: BreadcrumbItem[] = [];

    // Build breadcrumb trail from path segments
    let currentPath = '';
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      currentPath += `/${segment}`;

      // Skip UUID segments -- accumulate the path but don't add a crumb
      if (isUuid(segment)) {
        continue;
      }

      // Look up human-readable label
      const label = labelForSegment(segment);
      items.push({ label, href: currentPath });
    }

    return items;
  }, [location.pathname]);

  // Don't render if there are no breadcrumbs (root or hidden pattern)
  if (crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="hidden md:flex items-center gap-1 px-6 py-2 text-sm text-theme-tertiary bg-surface-primary border-b border-theme-primary"
    >
      <Link
        to="/"
        className="flex items-center hover:text-theme-primary transition-colors shrink-0"
        aria-label="Home"
      >
        <HomeIcon className="h-4 w-4" />
      </Link>

      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          <span key={crumb.href} className="flex items-center gap-1 min-w-0">
            <ChevronRightIcon className="h-3 w-3 shrink-0 text-theme-tertiary/60" />
            {isLast ? (
              <span className="font-medium text-theme-primary truncate" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className="hover:text-theme-primary transition-colors truncate"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
